/**
 * useApplyJobStatus - Hook for tracking audio apply jobs (animation choreography)
 *
 * Polls for active apply jobs and reconnects SSE when active jobs are detected.
 * This ensures that apply job subscriptions persist across navigation and page refresh.
 *
 * Pattern: Similar to useNarrationJobStatus for script generation tracking.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useVideoWorkspaceStore, type ApplyJobProgress } from '@/stores/videoWorkspaceStore';
import {
  getActiveAgentJobs,
  subscribeToAgentJob,
  type AgentJobProgress,
} from '@/services/agentJobService';

interface UseApplyJobStatusOptions {
  /** Poll interval in milliseconds (default: 3000ms) */
  pollInterval?: number;
  /** Whether polling is enabled (default: true) */
  enabled?: boolean;
}

export function useApplyJobStatus(
  conversationId: string | null,
  options: UseApplyJobStatusOptions = {}
) {
  const { pollInterval = 3000, enabled = true } = options;

  // Get apply job state from store (persists across navigation)
  const applyJobState = useVideoWorkspaceStore(
    (state) => conversationId
      ? state.applyJobStateByConversation[conversationId] ?? null
      : null
  );

  const setApplyJobState = useVideoWorkspaceStore(
    (state) => state.setApplyJobState
  );
  const updateSlideApplyJob = useVideoWorkspaceStore(
    (state) => state.updateSlideApplyJob
  );
  const clearApplyJobState = useVideoWorkspaceStore(
    (state) => state.clearApplyJobState
  );

  // Track active SSE subscriptions
  const subscriptionsRef = useRef<Map<string, () => void>>(new Map());
  const lastFetchRef = useRef<number>(0);

  // Track if there's currently an active apply operation
  const isApplyingAll = applyJobState?.isApplyingAll ?? false;
  const slideJobs = applyJobState?.slideJobs ?? {};
  const hasActiveJobs = Object.keys(slideJobs).length > 0;

  // Derive applyingSlideIds and slideApplyProgress from store state
  const applyingSlideIds = new Set(
    Object.entries(slideJobs)
      .filter(([_, job]) => job.status === 'PENDING' || job.status === 'PROCESSING')
      .map(([slideId]) => slideId)
  );

  const slideApplyProgress: Record<string, ApplyJobProgress> = {};
  for (const [slideId, job] of Object.entries(slideJobs)) {
    if (job.status === 'PENDING' || job.status === 'PROCESSING') {
      slideApplyProgress[slideId] = job.progress;
    }
  }

  // Track completed jobs to prevent re-subscribing (persists for session)
  const completedJobsRef = useRef<Set<string>>(new Set());

  // Helper to properly close an SSE subscription (call unsubscribe + remove from map)
  const closeSubscription = useCallback((jobId: string) => {
    const unsubscribe = subscriptionsRef.current.get(jobId);
    if (unsubscribe) {
      unsubscribe(); // Actually close the EventSource
    }
    subscriptionsRef.current.delete(jobId);
  }, []);

  // Subscribe to a job's SSE stream
  const subscribeToJob = useCallback((jobId: string, slideId: string) => {
    if (!conversationId) return;

    // Don't subscribe twice - check before doing anything
    if (subscriptionsRef.current.has(jobId)) {
      console.log(`[useApplyJobStatus] Already subscribed to job ${jobId}, skipping`);
      return;
    }

    // Don't subscribe to jobs we've already seen complete
    if (completedJobsRef.current.has(jobId)) {
      console.log(`[useApplyJobStatus] Job ${jobId} already completed, skipping subscription`);
      return;
    }

    console.log(`[useApplyJobStatus] Subscribing to job ${jobId} for slide ${slideId}`);

    const unsubscribe = subscribeToAgentJob(jobId, {
      onProgress: (progress: AgentJobProgress) => {
        // Don't update progress for jobs we've already seen complete
        // (prevents re-adding a removed job when onProgress fires after onStatusChange)
        if (completedJobsRef.current.has(jobId)) return;

        // Get fresh state from store to avoid stale closure issues
        const currentState = useVideoWorkspaceStore.getState();
        const currentSlideJob = currentState.applyJobStateByConversation[conversationId]?.slideJobs[slideId];

        updateSlideApplyJob(conversationId, slideId, {
          jobId,
          status: 'PROCESSING',
          progress: {
            thinking: progress.thinking,
            currentOperation: progress.currentOperation,
            percentage: progress.percentage,
          },
          startedAt: currentSlideJob?.startedAt ?? Date.now(),
        });
      },
      onComplete: () => {
        console.log(`[useApplyJobStatus] Job ${jobId} completed for slide ${slideId}`);
        // Mark as completed to prevent re-subscription and block further onProgress
        completedJobsRef.current.add(jobId);
        updateSlideApplyJob(conversationId, slideId, null);
        closeSubscription(jobId);

        // Check if this was the last job - auto-end Apply All
        // Get fresh state directly from store to avoid stale closure
        const currentState = useVideoWorkspaceStore.getState();
        const applyState = currentState.applyJobStateByConversation[conversationId];

        if (applyState?.isApplyingAll) {
          // Count remaining jobs (excluding the one we just completed)
          const remainingJobs = Object.keys(applyState.slideJobs || {}).filter(
            id => id !== slideId
          );

          if (remainingJobs.length === 0) {
            console.log('[useApplyJobStatus] Last job completed, auto-ending Apply All');
            setApplyJobState(conversationId, { isApplyingAll: false });

            // Dispatch custom event so UI can show toast
            window.dispatchEvent(new CustomEvent('apply-all-completed', {
              detail: { conversationId, timestamp: new Date().toISOString() }
            }));
          }
        }

        // Dispatch slides-updated event
        window.dispatchEvent(new CustomEvent('slides-updated', {
          detail: {
            conversationId,
            timestamp: new Date().toISOString(),
            source: 'animation_choreographer',
          }
        }));
      },
      onError: (error: string) => {
        console.error(`[useApplyJobStatus] Job ${jobId} error for slide ${slideId}:`, error);
        // Mark as completed to prevent re-subscription and block further onProgress
        completedJobsRef.current.add(jobId);
        updateSlideApplyJob(conversationId, slideId, null);
        closeSubscription(jobId);
      },
      onStatusChange: (status: string) => {
        if (status === 'COMPLETED' || status === 'FAILED') {
          // Mark as completed to prevent re-subscription and block further onProgress
          completedJobsRef.current.add(jobId);
          updateSlideApplyJob(conversationId, slideId, null);
          closeSubscription(jobId);
        }
      },
    });

    subscriptionsRef.current.set(jobId, unsubscribe);
  }, [conversationId, updateSlideApplyJob, setApplyJobState, closeSubscription]);

  // Fetch active apply jobs from API
  const fetchJobStatus = useCallback(async () => {
    if (!conversationId) return;

    // Debounce: don't fetch if less than 1 second since last fetch
    const now = Date.now();
    if (now - lastFetchRef.current < 1000) {
      return;
    }
    lastFetchRef.current = now;

    try {
      const response = await getActiveAgentJobs(conversationId);

      // Filter for animation_choreographer jobs that are actually active (PENDING or PROCESSING)
      const applyJobs = response.jobs.filter(
        job => job.agentName === 'animation_choreographer' &&
               (job.status === 'PENDING' || job.status === 'PROCESSING')
      );

      if (applyJobs.length > 0) {
        console.log('[useApplyJobStatus] Found active apply jobs:', applyJobs.length);

        // Update store state and subscribe to each job
        for (const job of applyJobs) {
          const slideId = job.metadata?.slideId as string;
          if (!slideId) continue;

          // Skip if we already have a subscription for this job
          if (subscriptionsRef.current.has(job.id)) {
            console.log(`[useApplyJobStatus] Already subscribed to job ${job.id}, skipping`);
            continue;
          }

          // Skip if we've already seen this job complete
          if (completedJobsRef.current.has(job.id)) {
            console.log(`[useApplyJobStatus] Job ${job.id} already completed, skipping`);
            continue;
          }

          // Update state
          updateSlideApplyJob(conversationId, slideId, {
            jobId: job.id,
            status: job.status as 'PENDING' | 'PROCESSING',
            progress: {
              thinking: job.progress?.thinking,
              currentOperation: job.progress?.currentOperation,
              percentage: job.progress?.percentage,
            },
            startedAt: new Date(job.startedAt || job.createdAt).getTime(),
          });

          // Subscribe to SSE stream
          subscribeToJob(job.id, slideId);
        }

        // Mark as applying all if there are multiple jobs
        if (applyJobs.length > 1) {
          setApplyJobState(conversationId, { isApplyingAll: true });
        }
      } else if (hasActiveJobs) {
        // No active jobs found but we thought we had some
        // This means the jobs completed while we were away
        console.log('[useApplyJobStatus] No active jobs found, clearing state');
        clearApplyJobState(conversationId);
      }
    } catch (error) {
      console.error('[useApplyJobStatus] Failed to fetch job status:', error);
    }
  }, [conversationId, hasActiveJobs, setApplyJobState, updateSlideApplyJob, clearApplyJobState, subscribeToJob]);

  // Initial fetch on mount and when conversationId changes
  useEffect(() => {
    if (!enabled || !conversationId) return;

    // Check for active jobs on mount
    fetchJobStatus();
  }, [conversationId, enabled, fetchJobStatus]);

  // Poll for updates when there are active jobs
  useEffect(() => {
    if (!enabled || !conversationId || !hasActiveJobs) return;

    console.log('[useApplyJobStatus] Starting poll for conversation:', conversationId);

    const intervalId = setInterval(fetchJobStatus, pollInterval);

    return () => {
      console.log('[useApplyJobStatus] Stopping poll');
      clearInterval(intervalId);
    };
  }, [conversationId, enabled, pollInterval, hasActiveJobs, fetchJobStatus]);

  // Cleanup subscriptions on unmount
  useEffect(() => {
    return () => {
      for (const unsubscribe of subscriptionsRef.current.values()) {
        unsubscribe();
      }
      subscriptionsRef.current.clear();
    };
  }, []);

  // Handle visibility change (tab switch back)
  useEffect(() => {
    if (!enabled || !conversationId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[useApplyJobStatus] Tab became visible, refetching status');
        fetchJobStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [conversationId, enabled, fetchJobStatus]);

  // Method to register a new job (called when starting apply)
  const registerJob = useCallback((slideId: string, jobId: string) => {
    if (!conversationId) return;

    updateSlideApplyJob(conversationId, slideId, {
      jobId,
      status: 'PENDING',
      progress: { currentOperation: 'Starting...', percentage: 0 },
      startedAt: Date.now(),
    });

    subscribeToJob(jobId, slideId);
  }, [conversationId, updateSlideApplyJob, subscribeToJob]);

  // Method to start Apply All mode
  const startApplyAll = useCallback(() => {
    if (!conversationId) return;
    setApplyJobState(conversationId, { isApplyingAll: true });
  }, [conversationId, setApplyJobState]);

  // Method to end Apply All mode
  const endApplyAll = useCallback(() => {
    if (!conversationId) return;
    setApplyJobState(conversationId, { isApplyingAll: false });
  }, [conversationId, setApplyJobState]);

  return {
    isApplyingAll,
    applyingSlideIds,
    slideApplyProgress,
    hasActiveJobs,
    refetch: fetchJobStatus,
    registerJob,
    startApplyAll,
    endApplyAll,
    clearState: useCallback(() => {
      if (conversationId) {
        clearApplyJobState(conversationId);
      }
    }, [conversationId, clearApplyJobState]),
  };
}

export default useApplyJobStatus;
